// Operational dashboard: every number is a filter link into the case list.
// Phone layout follows the "Lab OS" mockup: hero (name + live line + search/bell/+Case), a 2×2
// stat grid where ONLY the urgent tile carries colour, a 4-up compact strip, and the pipeline as
// collapsible grouped cards. Desktop keeps the wider 3-column layout.
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { WF_STAGE_ROLES as R } from '@alignova/shared';
import type { WfDashboard, WfEvent } from '@alignova/shared';
import { useLiveEvent } from '../../lib/useLive.ts';
import { PageHeader } from '../../ui/primitives.tsx';
import { Skeleton } from '../../ui/mobile.tsx';
import { IconAlert, IconCalendar, IconCases, IconCheck, IconChevronRight, IconProduction } from '../../ui/icons.tsx';
import { useWfConfig, wfApi } from './workflowApi.ts';
import { StatTile, fmtDT } from './wfUi.tsx';

const GROUP_DOT: Record<string, string> = {
  'Intake & records': '#3b82f6',
  Design: '#0d9488',
  'Approval & invoice': '#a855f7',
  'Production & dispatch': '#f59e0b',
  'Special queues': '#64748b',
  Other: '#94a3b8',
};

export function WfDashboardPage() {
  const nav = useNavigate();
  const cfg = useWfConfig();
  const [d, setD] = useState<WfDashboard | null>(null);
  const [activity, setActivity] = useState<WfEvent[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [expandAll, setExpandAll] = useState(false);
  // Wear is its own signal: a case can be perfectly on schedule in the lab and still be failing in
  // the patient's mouth. One small fetch so the flag is visible without opening the queue.
  const [lowWear, setLowWear] = useState(0);
  const load = useCallback(() => {
    wfApi.dashboard().then(setD).catch((e) => setErr(e.message));
    wfApi.activity(false, 25).then(setActivity).catch(() => undefined);
    wfApi.complianceQueue(true).then((q) => setLowWear(q.items.length)).catch(() => undefined);
  }, []);
  useEffect(load, [load]);
  useLiveEvent('workflow.changed', load);
  const go = (q: Record<string, unknown>) => nav(`/workflow/cases?${new URLSearchParams(Object.entries(q).filter(([, v]) => v !== undefined && v !== '').map(([k, v]) => [k, String(v)])).toString()}`);

  const groups = useMemo(() => {
    if (!d) return [] as Array<{ title: string; keys: string[] }>;
    const g: Array<{ title: string; keys: string[] }> = [
      { title: 'Intake & records', keys: [R.start, R.recordCheck, R.recordsDeficient, R.intake] },
      { title: 'Design', keys: [R.planning, R.design] },
      { title: 'Approval & invoice', keys: [R.doctorApproval, R.invoice] },
      { title: 'Production & dispatch', keys: [R.printing, R.packaging, R.dispatch] },
      { title: 'Special queues', keys: ['on_hold', 'retainers', 'twin_block', 'zirconia_crowns', 'appliances'] },
    ];
    const seen = new Set(g.flatMap((x) => x.keys));
    const others = d.byStage.filter((s) => !seen.has(s.stageKey) && !cfg.stageMap.get(s.stageKey)?.isTerminal);
    if (others.length) g.push({ title: 'Other', keys: others.map((s) => s.stageKey) });
    return g;
  }, [d, cfg]);

  if (err) return <div className="text-status-urgent-fg">{err}</div>;
  if (!d)
    return (
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-6">
          {Array.from({ length: 4 }, (_, i) => (
            <Skeleton key={i} className="h-28" />
          ))}
        </div>
        <Skeleton className="h-16" />
        <Skeleton className="h-40" />
      </div>
    );

  const needsAction = d.urgent + d.stuck;
  const updated = new Date(d.generatedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

  return (
    <div className="space-y-4 lg:space-y-6">
      {/* Desktop header; the phone hero lives in the workflow shell */}
      <div className="hidden lg:block">
        <PageHeader title="Dashboard" subtitle={`${d.activeCases} active cases · updated ${fmtDT(d.generatedAt)}`} />
      </div>
      <p className="text-xs text-muted lg:hidden">
        <span className="mr-1 inline-block h-2 w-2 rounded-full bg-accent align-middle" />
        {d.activeCases} active cases · updated {updated}
      </p>

      {lowWear > 0 && (
        <Link
          to="/workflow/compliance"
          className="tap-row flex items-center gap-3 rounded-xl border border-status-warn-fg/30 bg-status-warn-bg/60 px-4 py-3"
        >
          <span className="shrink-0 text-status-warn-fg">
            <IconAlert size={20} />
          </span>
          <span className="min-w-0 flex-1 text-sm text-status-warn-fg">
            <b className="tabnum">{lowWear}</b> aligner {lowWear === 1 ? 'patient is' : 'patients are'} behind on
            wear - worth a call before the next tray change.
          </span>
          <span className="shrink-0 text-status-warn-fg">
            <IconChevronRight size={18} />
          </span>
        </Link>
      )}

      {/* ---- Phone: 2×2 hero stats + 4-up strip ---- */}
      <section className="grid grid-cols-2 gap-3 lg:hidden">
        <HeroTile label="Active cases" value={d.activeCases} note={d.overdue ? `${d.overdue} overdue` : 'All on track'} noteTone={d.overdue ? 'bad' : 'good'} icon={IconCases} onClick={() => go({})} />
        <HeroTile
          label="Urgent / SLA"
          value={needsAction}
          action
          note={needsAction ? 'Review' : 'Nothing waiting'}
          onClick={() => go(d.stuck ? { stuck: 1 } : { priority: 'urgent,emergency' })}
        />
        <HeroTile label="Due tomorrow" value={d.dueTomorrow} note={`Due today: ${d.dueToday}`} noteTone={d.dueToday ? 'warn' : undefined} icon={IconCalendar} onClick={() => go({ due: 'tomorrow' })} />
        <HeroTile label="In production" value={d.inProduction} note={`${d.overdue} overdue`} noteTone={d.overdue ? 'bad' : undefined} icon={IconProduction} onClick={() => go({ group: 'production' })} />
        <div className="col-span-2">
          <HeroTile label="Completed (dispatched)" value={d.dispatchedTotal} note={`${d.dispatchedThisMonth} this month`} noteTone="good" icon={IconCheck} onClick={() => go({ stage: 'dispatched,delivered_completed', includeTerminal: 1 })} />
        </div>
      </section>
      <section className="grid grid-cols-4 divide-x divide-line rounded-xl border border-line bg-surface lg:hidden">
        <Mini label="Pt approval" value={d.awaitingApproval} onClick={() => go({ stage: R.doctorApproval })} />
        <Mini label="Dr review" value={d.awaitingReview} onClick={() => go({ group: 'review' })} />
        <Mini label="Stuck (>SLA)" value={d.stuck} onClick={() => go({ stuck: 1 })} bad={d.stuck > 0} />
        <Mini label="Dispatched" value={d.dispatchedToday} sub={`${d.dispatchedThisMonth} mo`} onClick={() => go({ stage: 'dispatched,delivered_completed', includeTerminal: 1 })} />
      </section>

      {/* ---- Desktop: four headline numbers, then the rest as a quieter strip ---- */}
      <section className="hidden space-y-3 lg:block">
        <div className="grid grid-cols-4 gap-3">
          <StatTile size="lg" label="Active cases" value={d.activeCases} tone="accent" sub="in the lab right now" onClick={() => go({})} />
          <StatTile size="lg" label="Overdue" value={d.overdue} tone="bad" sub={d.overdue ? 'past the promised date' : 'nothing late'} onClick={() => go({ due: 'overdue' })} />
          <StatTile size="lg" label="Due today" value={d.dueToday} tone="warn" sub={`${d.dueTomorrow} due tomorrow`} onClick={() => go({ due: 'today' })} />
          <StatTile size="lg" label="Stuck over SLA" value={d.stuck} tone="bad" sub={d.urgent ? `${d.urgent} urgent or emergency` : 'none sitting too long'} onClick={() => go({ stuck: 1 })} />
        </div>
        <div className="grid grid-cols-4 gap-3 xl:grid-cols-8">
          <StatTile label="Awaiting PT approval" value={d.awaitingApproval} onClick={() => go({ stage: R.doctorApproval })} />
          <StatTile label="Awaiting Dr review" value={d.awaitingReview} onClick={() => go({ group: 'review' })} />
          <StatTile label="Urgent / emergency" value={d.urgent} tone="warn" onClick={() => go({ priority: 'urgent,emergency' })} />
          <StatTile label="On hold" value={d.onHold} tone="warn" onClick={() => go({ stage: 'on_hold' })} />
          <StatTile label="In production" value={d.inProduction} onClick={() => go({ group: 'production' })} />
          <StatTile label="Ready for dispatch" value={d.readyForDispatch} tone="good" onClick={() => go({ stage: R.dispatch })} />
          <StatTile label="Dispatched today" value={d.dispatchedToday} tone="good" sub={`${d.dispatchedThisMonth} this month`} onClick={() => go({ stage: 'dispatched,delivered_completed', includeTerminal: 1 })} />
          <StatTile label="Completed (dispatched)" value={d.dispatchedTotal} tone="good" sub="all time" onClick={() => go({ stage: 'dispatched,delivered_completed', includeTerminal: 1 })} />
        </div>
      </section>

      {/* ---- Pipeline (both) ---- */}
      <section className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <div className="space-y-3 lg:col-span-2 lg:space-y-4">
          <div className="flex items-center justify-between lg:hidden">
            <h2 className="text-sm font-semibold text-ink">Aligner workflow pipeline</h2>
            <button className="tap text-sm font-medium text-accent" onClick={() => setExpandAll((v) => !v)}>
              {expandAll ? 'Collapse all' : 'Expand all'}
            </button>
          </div>
          {groups.map((g) => {
            const rows = g.keys.map((k) => ({ s: d.byStage.find((x) => x.stageKey === k), st: cfg.stageMap.get(k), k })).filter((x) => x.s && x.st);
            const total = rows.reduce((a, r) => a + (r.s?.count ?? 0), 0);
            return <PipelineGroup key={g.title} title={g.title} dot={GROUP_DOT[g.title] ?? '#94a3b8'} total={total} rows={rows} forceOpen={expandAll} onGo={(k) => go({ stage: k })} />;
          })}
        </div>

        <div className="space-y-4">
          <Panel title="My work">
            <div className="grid grid-cols-2 gap-2">
              <StatTile label="Open" value={d.myOpen} onClick={() => go({ preset: 'my' })} />
              <StatTile label="Due today" value={d.myDueToday} tone={d.myDueToday ? 'warn' : 'neutral'} onClick={() => go({ preset: 'my', due: 'today' })} />
              <StatTile label="Overdue" value={d.myOverdue} tone={d.myOverdue ? 'bad' : 'neutral'} onClick={() => go({ preset: 'my', due: 'overdue' })} />
              <StatTile label="Done today" value={d.myCompletedToday} tone="good" />
            </div>
          </Panel>
          <Panel title="Production">
            <div className="grid grid-cols-2 gap-2">
              <StatTile label="Printing" value={d.printing} onClick={() => go({ stage: R.printing })} />
              <StatTile label="Print complete" value={d.printComplete} onClick={() => go({ stage: R.packaging })} />
              <StatTile label="Reprints (active)" value={d.reprints} tone={d.reprints ? 'warn' : 'neutral'} />
              <StatTile label="Finishing" value={d.finishing} onClick={() => go({ stage: R.packaging })} />
              <StatTile label="Packaging" value={d.packaging} onClick={() => go({ stage: 'packaging' })} />
              <StatTile label="New today" value={d.newToday} tone="accent" />
            </div>
          </Panel>
          <Panel title="Recent activity">
            <ul className="max-h-96 space-y-1.5 overflow-y-auto text-xs">
              {activity.map((e) => (
                <li key={e.id} className="border-b border-line pb-1.5 last:border-0">
                  <div className="text-muted">
                    {fmtDT(e.at)} · {e.actorName ?? 'system'}
                  </div>
                  <Link to={`/workflow/cases/${e.caseId}`} className="text-ink hover:underline">
                    {e.eventType === 'stage_change' ? `${cfg.stageName(e.fromStage)} → ${cfg.stageName(e.toStage)}` : e.comment ?? e.eventType}
                  </Link>
                </li>
              ))}
              {!activity.length && <li className="text-muted">No activity yet.</li>}
            </ul>
          </Panel>
        </div>
      </section>
    </div>
  );
}

// --- pieces --------------------------------------------------------------------------------

function HeroTile({ label, value, note, noteTone, icon, action, onClick }: { label: string; value: number; note?: string; noteTone?: 'good' | 'warn' | 'bad'; icon?: (p: { size?: number }) => React.ReactNode; action?: boolean; onClick: () => void }) {
  const noteCls = noteTone === 'bad' ? 'bg-status-urgent-bg text-status-urgent-fg' : noteTone === 'warn' ? 'bg-status-warn-bg text-status-warn-fg' : noteTone === 'good' ? 'bg-accent/10 text-accent' : 'text-muted';
  if (action) {
    return (
      <button onClick={onClick} className="tap flex flex-col justify-between rounded-xl border border-status-warn-fg/30 bg-status-warn-bg p-4 text-left">
        <div className="flex items-start justify-between gap-2">
          <span className="flex items-center gap-1.5 text-[13px] font-semibold uppercase leading-tight tracking-wide text-status-warn-fg">
            <IconAlert size={15} />
            {label}
          </span>
          <span className="rounded-md bg-status-warn-fg px-2 py-0.5 text-[11px] font-bold text-status-warn-bg">Action</span>
        </div>
        <div className="mt-3 flex items-end justify-between">
          <span className="tabnum text-4xl font-extrabold text-status-warn-fg">{value}</span>
          <span className="text-sm font-semibold text-status-warn-fg underline underline-offset-2">{note}</span>
        </div>
      </button>
    );
  }
  return (
    <button onClick={onClick} className="tap flex flex-col justify-between rounded-xl border border-line bg-surface p-4 text-left shadow-level-1">
      <div className="flex items-start justify-between gap-2">
        <span className="text-[13px] font-semibold uppercase leading-tight tracking-wide text-muted">{label}</span>
        {icon && <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-accent/10 text-accent">{icon({ size: 18 })}</span>}
      </div>
      <div className="mt-3 flex items-end justify-between gap-2">
        <span className="tabnum text-4xl font-extrabold text-ink">{value}</span>
        {note && <span className={`rounded-md px-2 py-0.5 text-xs font-medium ${noteCls}`}>{note}</span>}
      </div>
    </button>
  );
}

function Mini({ label, value, sub, bad, onClick }: { label: string; value: number; sub?: string; bad?: boolean; onClick: () => void }) {
  return (
    <button onClick={onClick} className="tap flex flex-col items-center justify-center px-1 py-3 text-center">
      <span className="text-[11px] font-semibold uppercase leading-tight tracking-wide text-muted">{label}</span>
      <span className={`tabnum mt-1 text-xl font-extrabold ${bad ? 'text-status-urgent-fg' : 'text-ink'}`}>{value}</span>
      {sub && <span className="text-[11px] text-muted">{sub}</span>}
    </button>
  );
}

function PipelineGroup({ title, dot, total, rows, forceOpen, onGo }: { title: string; dot: string; total: number; rows: Array<{ k: string; s: { count: number; overdue: number; stuck: number } | undefined; st: { name: string; color?: string | null } | undefined }>; forceOpen: boolean; onGo: (k: string) => void }) {
  const [open, setOpen] = useState(total > 0);
  const isOpen = forceOpen || open;
  const active = total > 0;
  return (
    <div className={`overflow-hidden rounded-xl border bg-surface shadow-level-1 ${active ? 'border-accent/50' : 'border-line'}`}>
      <button onClick={() => setOpen((v) => !v)} className="tap flex w-full items-center justify-between px-4 py-3 text-left lg:cursor-default" aria-expanded={isOpen}>
        <span className="flex items-center gap-2 text-sm font-semibold text-ink">
          <span className="h-2.5 w-2.5 rounded-full" style={{ background: dot }} />
          {title}
        </span>
        <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${active ? 'bg-accent text-accent-fg' : 'bg-surface-2 text-muted'}`}>
          {active ? `${total} active` : '0 total'}
        </span>
      </button>
      {(isOpen || typeof window === 'undefined' || window.innerWidth >= 1024) && (
        <div className={`grid grid-cols-2 gap-2 border-t border-line px-3 pb-3 pt-3 sm:grid-cols-3 md:grid-cols-4 ${isOpen ? '' : 'hidden lg:grid'}`}>
          {rows.map(({ k, s, st }) => {
            const on = (s?.count ?? 0) > 0;
            return (
              <button key={k} onClick={() => onGo(k)} className={`tap flex items-center justify-between rounded-xl border px-3 py-2.5 text-left ${on ? 'border-accent/40 bg-accent/5' : 'border-line'}`}>
                <span className="flex min-w-0 items-center gap-2 text-sm">
                  <span className="h-2 w-2 shrink-0 rounded-full" style={{ background: st?.color ?? '#64748B' }} />
                  <span className={`truncate ${on ? 'font-semibold text-ink' : 'text-ink'}`}>{st?.name}</span>
                </span>
                <span className="flex shrink-0 items-center gap-1">
                  {(s?.overdue ?? 0) > 0 && <span className="rounded bg-status-urgent-bg px-1 text-[10px] font-bold text-status-urgent-fg" title="overdue">{s!.overdue}</span>}
                  {(s?.stuck ?? 0) > 0 && <span className="tabnum rounded bg-status-warn-bg px-1 text-[10px] font-bold text-status-warn-fg" title="over SLA">{s!.stuck} SLA</span>}
                  <span className={`rounded-md px-1.5 font-mono text-base font-bold ${on ? 'bg-accent/15 text-ink' : 'text-ink'}`}>{s?.count ?? 0}</span>
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function Panel({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-xl border border-line bg-surface p-3 shadow-level-1 lg:rounded-lg">
      <div className="mb-2 text-xs font-semibold text-muted">{title}</div>
      {children}
    </div>
  );
}
